<template>
    <div class="page-wrapper manage-access-page">
        <div class="d-flex align-items-center justify-content-between">
            <ExplanationTitle heading="Manage Access" explanation=""/>
            <button class="btn btn-primary" @click.prevent="openInvitePopup">Add Guest User</button>
        </div>

        <div class="mt-4">
            <div class="table-wrapper">
                <table class="table guests-table">
                    <thead>
                    <tr>
                        <th>Guest</th>
                        <th>Email</th>
                        <th>Projects Access</th>
                        <th></th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-if="loaded && guests.length === 0">
                        <td colspan="4" class="text-center py-5">
                            <h4 class="font-weight-bold">No guest users</h4>
                            <p>Add a guest to give them read-only access to your projects.</p>
                        </td>
                    </tr>
                    <tr v-if="!loaded">
                        <td colspan="4">
                            <div class="spinner-border opacity-25"></div>
                        </td>
                    </tr>
                    <tr v-for="(guest) in guests" :key="guest.id">
                        <td>
                            <div class="guest-info">
                                <span :class="{'guest-avatar': true}" :style="{backgroundColor: guest.avatar.color}">
                                    <span :class="{'text-light': guest.avatar.text === 'light', 'text-dark': guest.avatar.text === 'dark'}">{{ guest.avatar.credentials }}</span>
                                </span>
                                <strong>{{ guest.name }}</strong>
                            </div>
                        </td>
                        <td>{{ guest.email }}</td>
                        <td>
                            <div class="projects-popover-wrapper" v-if="guest.projects.length > 0">
                                <span class="projects-trigger">{{ guest.projects.length }} {{ guest.projects.length === 1 ? 'project' : 'projects' }}</span>
                                <div class="projects-popover">
                                    <div class="projects-popover-list">
                                        <span class="project-badge" v-for="(project) in guest.projects" :key="project.hashId">{{ project.name }}</span>
                                    </div>
                                    <a href="#" class="action-btn mt-2" @click.prevent="editGuest(guest)">Manage Projects</a>
                                </div>
                            </div>
                            <span class="text-muted" v-else>No projects</span>
                        </td>
                        <td class="td-actions">
                            <div class="actions-row">
                                <a href="#" class="action-btn" @click.prevent="openChangeEmail(guest)">Change Email</a>
                                <a href="#" class="action-btn" @click.prevent="openChangePassword(guest)">Change Password</a>
                                <a href="#" class="action-btn action-btn-danger" @click.prevent="removeGuest(guest)">Remove Access</a>
                            </div>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- Invite Guest Popup -->
        <Popup :class="{'active': showInvitePopup}">
            <InviteGuestPopup :editGuest="editingGuest" ref="invitePopup"/>
        </Popup>

        <!-- Edit Guest Popup (Change Email / Change Password) -->
        <Popup :class="{'active': showEditPopup}">
            <EditGuestPopup :mode="editPopupMode" :guest="editPopupGuest" ref="editPopup" v-if="editPopupGuest"/>
        </Popup>
    </div>
</template>

<script>
import ExplanationTitle from "@/components/widgets/ExplanationTitle.vue";
import Popup from "@/components/widgets/Popup.vue";
import InviteGuestPopup from "@/components/guests/InviteGuestPopup.vue";
import EditGuestPopup from "@/components/guests/EditGuestPopup.vue";
import repository from "@/repository/repository";

export default {
    name: "ManageAccess",
    components: {EditGuestPopup, InviteGuestPopup, Popup, ExplanationTitle},
    metaInfo: {
        title: "Manage Access",
    },
    data() {
        return {
            loaded: false,
            guests: [],
            showInvitePopup: false,
            editingGuest: null,
            showEditPopup: false,
            editPopupMode: 'email',
            editPopupGuest: null,
        }
    },
    created() {
        this.loadGuests();
        this.$root.$on('reloadGuests', this.loadGuests);
        this._onPopstate = () => {
            if (this.showInvitePopup) {
                this.showInvitePopup = false;
                this.editingGuest = null;
                document.documentElement.style.overflow = "auto";
                if (this.$refs.invitePopup) this.$refs.invitePopup.reset();
            }
            if (this.showEditPopup) {
                this.showEditPopup = false;
                this.editPopupGuest = null;
                document.documentElement.style.overflow = "auto";
                if (this.$refs.editPopup) this.$refs.editPopup.reset();
            }
        };
        window.addEventListener('popstate', this._onPopstate);
    },
    beforeDestroy() {
        this.$root.$off('reloadGuests', this.loadGuests);
        this.$root.$off('closePopups', this.closePopup);
        window.removeEventListener('popstate', this._onPopstate);
    },
    mounted() {
        this.$root.$on('closePopups', this.closePopup);
    },
    methods: {
        loadGuests() {
            this.loaded = false;
            repository.get("/guests")
                .then(response => {
                    this.guests = response.data.guests;
                    this.loaded = true;
                })
                .catch(error => {
                    console.log(error);
                    this.loaded = true;
                });
        },
        openInvitePopup() {
            this.editingGuest = null;
            history.pushState({page: 1}, "", this.$route.path);
            this.showInvitePopup = true;
            document.documentElement.style.overflow = "hidden";
            if (this.$refs.invitePopup) this.$refs.invitePopup.reset();
        },
        editGuest(guest) {
            this.editingGuest = guest;
            history.pushState({page: 1}, "", this.$route.path);
            this.showInvitePopup = true;
            document.documentElement.style.overflow = "hidden";
            this.$nextTick(() => {
                this.$root.$emit('editGuestData', guest);
            });
        },
        closePopup() {
            if (this.showInvitePopup) {
                this.showInvitePopup = false;
                this.editingGuest = null;
                document.documentElement.style.overflow = "auto";
                if (this.$refs.invitePopup) this.$refs.invitePopup.reset();
            }
            if (this.showEditPopup) {
                this.showEditPopup = false;
                this.editPopupGuest = null;
                document.documentElement.style.overflow = "auto";
                if (this.$refs.editPopup) this.$refs.editPopup.reset();
            }
        },
        openChangeEmail(guest) {
            this.editPopupMode = 'email';
            this.editPopupGuest = guest;
            history.pushState({page: 1}, "", this.$route.path);
            this.showEditPopup = true;
            document.documentElement.style.overflow = "hidden";
            this.$nextTick(() => {
                if (this.$refs.editPopup) this.$refs.editPopup.reset();
                this.$refs.editPopup.email = guest.email;
            });
        },
        openChangePassword(guest) {
            this.editPopupMode = 'password';
            this.editPopupGuest = guest;
            history.pushState({page: 1}, "", this.$route.path);
            this.showEditPopup = true;
            document.documentElement.style.overflow = "hidden";
            this.$nextTick(() => {
                if (this.$refs.editPopup) this.$refs.editPopup.reset();
            });
        },
        removeGuest(guest) {
            this.$confirm({
                title: "Remove guest user?",
                message: "By confirming, " + guest.name + " will lose access to all shared projects. This action cannot be undone.",
                button: {
                    yes: "Yes, remove",
                    no: "No, cancel"
                },
                callback: confirm => {
                    if (confirm) {
                        repository.delete("/guests/" + guest.id)
                            .then(() => {
                                this.loadGuests();
                            })
                            .catch(error => {
                                console.log(error);
                            })
                    }
                }
            })
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../scss/variables";

.manage-access-page {
}

.table-wrapper {
    @include tablet {
        overflow: scroll;
        white-space: nowrap;
    }

    @include smartphone {
        overflow: scroll;
        white-space: nowrap;
    }
}

.table.guests-table {
    margin-bottom: 0;

    thead {
        tr {
            th {
                padding: 0 1rem 1.5rem 0;
                color: $dark;
                border-top: none;
                border-bottom: none;
                font-size: 1rem;
                font-weight: 600;
            }
        }
    }

    tbody {
        tr {
            td {
                padding: 1.25rem 1rem 1.25rem 0;
                vertical-align: middle;

                .guest-info {
                    display: flex;
                    align-items: center;
                    white-space: nowrap;

                    .guest-avatar {
                        width: 2.25rem;
                        height: 2.25rem;
                        flex-shrink: 0;
                        border-radius: 50%;
                        line-height: 2.25rem;
                        text-align: center;
                        font-weight: bold;
                        font-size: 0.8rem;
                        margin-right: 0.5rem;
                        text-transform: uppercase;
                    }

                    strong {
                        font-weight: 600;
                        flex-shrink: 0;
                    }
                }

                .projects-popover-wrapper {
                    position: relative;
                    display: inline-block;

                    .projects-trigger {
                        font-weight: 500;
                        color: $primary;
                        cursor: default;
                        border-bottom: 1px dashed rgba($primary, 0.4);
                    }

                    .projects-popover {
                        display: none;
                        position: absolute;
                        top: calc(100% + 0.5rem);
                        left: 0;
                        z-index: 10;
                        background: $white;
                        border: 1px solid $border-grey;
                        border-radius: $box-border-radius;
                        box-shadow: 0 4px 16px rgba($dark, 0.1);
                        padding: 0.75rem;
                        min-width: 200px;
                        white-space: normal;

                        &::before {
                            content: '';
                            position: absolute;
                            top: -0.5rem;
                            left: 0;
                            right: 0;
                            height: 0.5rem;
                        }
                    }

                    &:hover .projects-popover {
                        display: block;
                    }

                    .projects-popover-list {
                        .project-badge {
                            margin-bottom: 0.25rem;
                        }
                    }
                }

                .project-badge {
                    display: inline-block;
                    font-size: 0.85rem;
                    font-weight: 500;
                    padding: 0.3rem 0.6rem;
                    border-radius: $box-border-radius;
                    background: rgba($primary, 0.1);
                    color: $primary;
                    margin-right: 0.25rem;
                }

                &.td-actions {
                    text-align: right;

                    .actions-row {
                        display: flex;
                        justify-content: flex-end;
                        white-space: nowrap;
                        flex-wrap: nowrap;
                    }
                }

                .action-btn {
                    display: inline-block;
                    padding: 0.4rem 0.75rem;
                    background: $background-grey;
                    border-radius: $box-border-radius;
                    border: 1px solid transparent;
                    text-decoration: none;
                    color: $dark;
                    font-size: 0.85rem;
                    font-weight: 500;
                    margin-right: 0.25rem;
                    white-space: nowrap;
                    @extend .animated;

                    &:hover {
                        background: lighten($primary, 50%);
                        color: $primary;
                    }

                    &.action-btn-danger {
                        background: rgba($danger, 0.1);
                        color: $danger;
                        margin-right: 0;

                        &:hover {
                            background: $danger;
                            color: $white;
                        }
                    }
                }

                .spinner-border {
                    width: 1.5rem;
                    height: 1.5rem;
                    border-color: $border-dark-grey;
                    border-right-color: transparent;
                    border-width: 2px;
                    margin-top: 0.5rem;
                }
            }
        }
    }
}
</style>
